import {
  calculateFilterKey,
  normalizeHeader,
  parseLiteralDateTime,
} from '../domain/businessRules'
import { buildPublicationMetrics, type ImportSourceRow, type PublicationMetric } from './aggregate'

type CanonicalColumn =
  | 'ID da sessão'
  | 'Data do início da sessão'
  | 'Nome do operador'
  | 'Fila'
  | 'Conversa com operador'
  | 'Espera em fila + Espera operador'

interface ColumnDefinition {
  name: CanonicalColumn
  aliases: string[]
  required?: boolean
  important?: boolean
}

const COLUMN_DEFINITIONS: ColumnDefinition[] = [
  { name: 'ID da sessão', aliases: ['ID da sessão', 'ID da sessao'], required: true },
  {
    name: 'Data do início da sessão',
    aliases: ['Data do início da sessão', 'Data do inicio da sessao'],
    required: true,
  },
  { name: 'Nome do operador', aliases: ['Nome do operador'], important: true },
  { name: 'Fila', aliases: ['Fila'], important: true },
  { name: 'Conversa com operador', aliases: ['Conversa com operador'], important: true },
  {
    name: 'Espera em fila + Espera operador',
    aliases: ['Espera em fila + Espera operador'],
    important: true,
  },
]

const aliasMap = new Map<string, CanonicalColumn>()
const definitionByName = new Map(COLUMN_DEFINITIONS.map(definition => [definition.name, definition]))
for (const definition of COLUMN_DEFINITIONS) {
  for (const alias of definition.aliases) aliasMap.set(normalizeHeader(alias), definition.name)
}

function blank(value: unknown): boolean {
  return value === null || value === undefined || String(value).trim() === ''
}

function scoreHeaderRow(row: unknown[]) {
  let hits = 0
  let weighted = 0
  const seen = new Set<CanonicalColumn>()
  for (const value of row ?? []) {
    const canonical = aliasMap.get(normalizeHeader(value))
    if (!canonical || seen.has(canonical)) continue
    seen.add(canonical)
    hits += 1
    const definition = definitionByName.get(canonical)
    weighted += definition?.required ? 5 : definition?.important ? 3 : 1
  }
  return { hits, weighted }
}

export function findHeaderIndex(rows: unknown[][], scanRows = 20): number {
  let best: { index: number; hits: number; weighted: number } | null = null
  const limit = Math.min(scanRows, rows.length)
  for (let index = 0; index < limit; index += 1) {
    const score = scoreHeaderRow(rows[index] ?? [])
    if (!best || score.weighted > best.weighted) best = { index, ...score }
  }
  if (!best || best.hits < 4) {
    throw new Error('Não foi possível identificar com segurança a linha de cabeçalho.')
  }
  return best.index
}

function isDescriptionRow(row: unknown[], index: Map<CanonicalColumn, number>): boolean {
  const session = index.get('ID da sessão')
  const startedAt = index.get('Data do início da sessão')
  if (!blank(session === undefined ? '' : row[session]) || !blank(startedAt === undefined ? '' : row[startedAt])) {
    return false
  }

  const phrases = [
    'sessão fechada por operador',
    'conversa aberta com usuário',
    'mensagens do operador',
    'média de espera',
    'tempo do usuário',
    'transferência sem mensagens',
    'closed without messages',
    'inactivity rule',
  ]
  const content = row.filter(value => !blank(value)).map(value => String(value).toLowerCase())
  const text = content.join(' | ')
  return phrases.some(phrase => text.includes(phrase)) || content.length >= 5
}

export interface PreparedRows {
  rows: ImportSourceRow[]
  headerIndex: number
  recognizedColumns: CanonicalColumn[]
  missingRequired: CanonicalColumn[]
  missingImportant: CanonicalColumn[]
  ignoredRows: number
  descriptionRows: number
  repeatedHeaderRows: number
  unrecognizedRows: number
}

export function prepareMatrix(rows: unknown[][]): PreparedRows {
  const headerIndex = findHeaderIndex(rows)
  const header = rows[headerIndex] ?? []
  const index = new Map<CanonicalColumn, number>()

  header.forEach((value, columnIndex) => {
    const canonical = aliasMap.get(normalizeHeader(value))
    if (canonical && !index.has(canonical)) index.set(canonical, columnIndex)
  })

  const missingRequired = COLUMN_DEFINITIONS
    .filter(definition => definition.required && !index.has(definition.name))
    .map(definition => definition.name)
  const missingImportant = COLUMN_DEFINITIONS
    .filter(definition => definition.important && !index.has(definition.name))
    .map(definition => definition.name)

  let ignoredRows = headerIndex + 1
  let descriptionRows = 0
  let repeatedHeaderRows = 0
  let unrecognizedRows = 0
  const accepted: ImportSourceRow[] = []

  const headerHits = scoreHeaderRow(header).hits
  for (let rowIndex = headerIndex + 1; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] ?? []
    if (row.every(blank)) {
      ignoredRows += 1
      continue
    }

    if (scoreHeaderRow(row).hits >= Math.max(6, Math.floor(headerHits * 0.6))) {
      ignoredRows += 1
      repeatedHeaderRows += 1
      continue
    }

    if (isDescriptionRow(row, index)) {
      ignoredRows += 1
      descriptionRows += 1
      continue
    }

    const sessionIndex = index.get('ID da sessão')
    const startedIndex = index.get('Data do início da sessão')
    const sessionValue = sessionIndex === undefined ? '' : row[sessionIndex]
    const startedValue = startedIndex === undefined ? '' : row[startedIndex]
    if (blank(sessionValue) && blank(startedValue)) {
      ignoredRows += 1
      unrecognizedRows += 1
      continue
    }

    const value = (column: CanonicalColumn) => {
      const columnIndex = index.get(column)
      return columnIndex === undefined ? '' : row[columnIndex]
    }

    accepted.push({
      startedAt: value('Data do início da sessão'),
      operator: value('Nome do operador'),
      queue: value('Fila'),
      conversationSeconds: value('Conversa com operador'),
      waitingSeconds: value('Espera em fila + Espera operador'),
    })
  }

  return {
    rows: accepted,
    headerIndex,
    recognizedColumns: [...index.keys()],
    missingRequired,
    missingImportant,
    ignoredRows,
    descriptionRows,
    repeatedHeaderRows,
    unrecognizedRows,
  }
}

export interface PeriodCandidate {
  referenceMonth: string
  sourceRowCount: number
  publishableRowCount: number
  metrics: PublicationMetric[]
}

export function buildPeriodCandidates(rows: ImportSourceRow[]): PeriodCandidate[] {
  const grouped = new Map<string, ImportSourceRow[]>()

  for (const row of rows) {
    const parsed = parseLiteralDateTime(row.startedAt)
    if (!parsed) continue
    const referenceMonth = `${parsed.year}-${String(parsed.month).padStart(2, '0')}-01`
    const periodRows = grouped.get(referenceMonth) ?? []
    periodRows.push(row)
    grouped.set(referenceMonth, periodRows)
  }

  return [...grouped.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([referenceMonth, periodRows]) => ({
      referenceMonth,
      sourceRowCount: periodRows.length,
      publishableRowCount: periodRows.filter(row =>
        calculateFilterKey(parseLiteralDateTime(row.startedAt)) === 'OK',
      ).length,
      metrics: buildPublicationMetrics(periodRows),
    }))
}
